import { jsPDF } from 'jspdf';
import * as htmlToImage from 'html-to-image';
import { loadImageAsPngDataUrlForPdf } from './exportUtils';

type ExportItem = { key: string; title: string };

function nextFrame(times = 2) {
  return new Promise<void>(resolve => {
    const step = (n: number) => {
      if (n <= 0) return resolve();
      requestAnimationFrame(() => step(n - 1));
    };
    step(times);
  });
}

/**
 * Get natural dimensions of an image from its data URL.
 */
function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

export async function exportDashboardChartsToPdf(opts: {
  items: ExportItem[];
  fileNameBase: string;
  pageOrientation?: 'p' | 'l';
  companyLogoUrl?: string | null;
  companyName?: string | null;
}) {
  const { items, fileNameBase, pageOrientation = 'p', companyLogoUrl, companyName } = opts;

  const pdf = new jsPDF({
    orientation: pageOrientation,
    unit: 'pt',
    format: 'a4',
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 36;
  const headerSpace = 22;

  const timestamp = new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/[:-]/g, '')
    .replace('T', '_');

  const fullFileName = `${fileNameBase}_${timestamp}.pdf`;

  let brandingPng: string | null = null;
  if (companyLogoUrl?.trim()) {
    brandingPng = await loadImageAsPngDataUrlForPdf(companyLogoUrl.trim());
  }
  const companyLine = companyName?.trim() || '';

  const stage = document.getElementById('dashboard-export-stage');
  if (!stage) throw new Error('Export stage not found');

  for (let i = 0; i < items.length; i++) {
    const { key, title } = items[i];

    const node = stage.querySelector(
      `[data-export-key="${key}"]`,
    ) as HTMLElement | null;
    if (!node) continue;

    await nextFrame(2);

    const rect = node.getBoundingClientRect();
    const nodeW = Math.max(1, rect.width);
    const nodeH = Math.max(1, rect.height);

    const isGrid =
      key === 'financialApplicationsScope' ||
      key === 'listingOfProcessOwnersByProcess' ||
      key === 'processOwnerCertificationCompletionStatus';

    const pixelRatio = isGrid ? 1 : 2;

    try {
      const dataUrl = await htmlToImage.toPng(node, {
        cacheBust: true,
        pixelRatio,
        skipFonts: true,
        width: Math.ceil(nodeW),
        height: Math.ceil(nodeH),
      });

      if (i > 0) pdf.addPage();

      const LOGO_HEIGHT = 30;
      const LOGO_MAX_WIDTH = 90;
      let titleY = margin;
      if (brandingPng || companyLine) {
        let xPos = margin;
        if (brandingPng) {
          let logoW = LOGO_HEIGHT;
          try {
            const dims = await getImageDimensions(brandingPng);
            const ratio = dims.width / dims.height;
            logoW = Math.min(LOGO_HEIGHT * ratio, LOGO_MAX_WIDTH);
          } catch { /* fallback to square */ }
          pdf.addImage(brandingPng, 'PNG', xPos, margin, logoW, LOGO_HEIGHT);
          xPos += logoW + 3;
        }
        if (companyLine) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          const textY = brandingPng ? margin + LOGO_HEIGHT / 2 + 3 : margin + 10;
          pdf.text(companyLine, xPos, textY);
        }
        titleY = margin + (brandingPng ? LOGO_HEIGHT + 12 : 27);
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text(title, margin, titleY);

      const brandingHeight = titleY - margin + headerSpace;
      const usableW = pageWidth - margin * 2;
      const usableH = pageHeight - margin * 2 - brandingHeight;

      const scale = Math.min(usableW / nodeW, usableH / nodeH);
      const renderW = nodeW * scale;
      const renderH = nodeH * scale;

      const x = margin + (usableW - renderW) / 2;
      const y = titleY + headerSpace;

      pdf.addImage(dataUrl, 'PNG', x, y, renderW, renderH);

      await nextFrame(1);
      await new Promise(r => setTimeout(r, 25));
    } catch {
      if (i > 0) pdf.addPage();
      const LOGO_PT_ERR = 22;
      let titleY = margin;
      if (brandingPng || companyLine) {
        let xPos = margin;
        if (brandingPng) {
          pdf.addImage(brandingPng, 'PNG', xPos, margin, LOGO_PT_ERR, LOGO_PT_ERR);
          xPos += LOGO_PT_ERR + 6;
        }
        if (companyLine) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          const textY = brandingPng ? margin + LOGO_PT_ERR / 2 + 3 : margin + 10;
          pdf.text(companyLine, xPos, textY);
        }
        titleY = margin + (brandingPng ? LOGO_PT_ERR + 6 : 16);
      }
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text(title, margin, titleY);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.text('Export failed for this chart.', margin, titleY + 20);
    }
  }

  pdf.save(fullFileName);
}
